import {soundAssets} from "../../config/config";
import InstrumentSamplePlayer from "./InstrumentSamplePlayer";
import {getAudioContext} from '../audioContext';
import {ISchedulePlayNote} from "./ISchedulePlayNote";
import {subscribeToSettingsEvents} from "../eventBus/settings/settingsEventBus";
import {SettingsEventTypes} from "../eventBus/settings/SettingsEventTypes";
import settingsService from "../settings/settingsService";
import {logger} from "../log/logger";
/**
 * We use a hash/map of midi number to sample player, so any time a midi note is played, we can ask it's associated sample player to play the sample associated with the midi note.
 */
interface IMidiInstrumentSamplePlayerHash{
  [midi: number]: InstrumentSamplePlayer;
}

/**
 * Synthesizer is used to play various Instrument Samples, or potentially oscillators, etc in the future.
 * Ultimately we will want to say Synthesizer synthesize guitar, piano, harp, etc, and Synthesizer will hide all the implementation details of how it's done.
 * For now Synthesizer assumes we're playing the IOWA piano samples.
 */
export default class Synthesizer implements ISchedulePlayNote{
  midiInstrumentSamplePlayerHash: IMidiInstrumentSamplePlayerHash = {};
  volume = 1;
  gainNode: GainNode;
  constructor({destination}: {destination?: AudioNode} = {}) {
    void this.createAndLoadWebAudioNodesAndSamples(destination);

    subscribeToSettingsEvents((message: SettingsEventTypes)=>{
      switch (message.type) {
        case "audioContextSampleRateChanged":
          logger.lessonAudio.debug({message: `synthesizer recreating samples for new audio context sample rate`});
          void this.createAndLoadWebAudioNodesAndSamples();
          break;
        case "currentlySelectedSoundLibraryChanged":
          void this.createAndLoadWebAudioNodesAndSamples();
          break;
      }
    });
  }

  async createAndLoadWebAudioNodesAndSamples(destination: AudioNode = getAudioContext().destination){
    this.gainNode = createConnectedGainNode({context: getAudioContext(), destination, volume: this.volume});
    this.midiInstrumentSamplePlayerHash = createMidiInstrumentSamplePlayerHash(getAudioContext(), this.gainNode, settingsService.getCurrentlySelectedSoundLibrary());
    try{
      await loadInstrumentSamples(this.midiInstrumentSamplePlayerHash);
    }catch(e){
      logger.lessonAudio.error({message: `synthesizer loadInstrumentSamples errored: `, error: e as Error});
    }

  }

  setVolume(volume: number){
    this.volume = volume;
    this.gainNode.gain.value = volume;
  }

  enable(){
    this.gainNode.gain.value = this.volume;
  }
  disable(){
    this.gainNode.gain.value = 0;
  }

  /**
   * Play a midi note with provided loudness/velocity.
   * @param midi - midi number representation of the note.
   * @param velocity - value between 0-127
   */
  playNote({midi, velocity}: {midi: number; velocity: number}){
    //find the associated sample player
    const instrumentSamplePlayer = this.midiInstrumentSamplePlayerHash[midi];
    if(!instrumentSamplePlayer){
      logger.lessonAudio.warn({message: `no instrument sample for midi: ${midi}`});
      return;
    }
    //convert velocity to volume.
    const volume = convertMidiVelocityToVolume(velocity);
    //ask the sample player to play the note.
    try{
      instrumentSamplePlayer.play({volume});
    }catch(e){
      logger.lessonAudio.error({message: `file's probably not loaded yet. ignore for now.`, error: e as Error});
    }
  }

  /**
   * Stop playing a midi note, but allow the release envelope to continue. i.e. not an abrupt stop to playing the sample. more like pressing and releasing a piano key.
   * @param midi - midi number representation of the note.
   */
  releaseNote({midi}: {midi: number}){
    const instrumentSamplePlayer = this.midiInstrumentSamplePlayerHash[midi];
    if(!instrumentSamplePlayer){
      logger.lessonAudio.warn({message: `no instrument sample for midi: ${midi}`})
      return ;
    }
    try{
      instrumentSamplePlayer.release();
    }catch(e){
      logger.lessonAudio.error({message: `files probably not loaded yet. ignore for now: `, error: e as Error});
    }
  }

  schedulePlayNote({startAtContextRelativeSeconds, durationInSeconds, velocity, midi}: {midi: number; velocity: number; startAtContextRelativeSeconds: number; durationInSeconds: number}){
    //find the associated sample player
    const instrumentSamplePlayer = this.midiInstrumentSamplePlayerHash[midi];
    if(!instrumentSamplePlayer){
      logger.lessonAudio.warn({message: `no instrument sample for midi: ${midi}`});
      // return to exit
      return ;
    }
    //convert velocity to volume.
    const volume = convertMidiVelocityToVolume(velocity);
    //ask the sample player to play the note.
    try{
      instrumentSamplePlayer.schedulePlay({volume, durationInSeconds, startAtContextRelativeSeconds});
    }catch(e){
      logger.lessonAudio.error({message: `file's probably not loaded yet. ignore for now.`, error: e as Error});
    }
  }

  stopAllScheduledPlays(){
    //iterate over each midiInstrumentSamplePlayer and stop all scheduled plays
    Object.entries(this.midiInstrumentSamplePlayerHash).forEach(([_midi, samplePlayer]) => samplePlayer.stopAllScheduledPlays());
  }
}

function createMidiInstrumentSamplePlayerHash(context: AudioContext, destination: AudioNode, library: string): IMidiInstrumentSamplePlayerHash{
  let {baseUrl, piano: {alicia: {sampleFileNames: { fileType, lowestQualityRelativePath, mediumQualityRelativePath, midiHash, }, pianoSoundsRelativePath}}} = soundAssets;
  let relativePath = lowestQualityRelativePath;
  switch (library) {
    case "Guitar 44k Low Quality":
      return createMidiInstrumentSamplePlayerHashForOrangeTreeAcousticGuitar(destination);
    case "Alicia 48k Medium Quality":
      relativePath = mediumQualityRelativePath;
      break;
    case "Alicia 48k Low Quality":
      relativePath = lowestQualityRelativePath;
      break;
    case "Legacy 44.1k":
      ({baseUrl, piano: {legacy: {sampleFileNames: { fileType, lowestQualityRelativePath, midiHash, }, pianoSoundsRelativePath}}} = soundAssets);
      relativePath = lowestQualityRelativePath;
  }
  const result = Object.entries(midiHash).reduce( (agg: IMidiInstrumentSamplePlayerHash, [midi, midiFileNamePart]) => {
    const url = `${baseUrl}${pianoSoundsRelativePath}${relativePath}${midiFileNamePart}${fileType}`.replace(`#`, `%23`);
    const midiNumber = parseInt(midi); //typescript incorrectly assumes that Object.entries uses string as property names.
    agg[midiNumber] = new InstrumentSamplePlayer({url, destination});
    return agg;
  }, {});
  return result;
}

/**
 * Downloads sound files by iterating over entries in the provided hash and calling load, which results in an ajax/fetch call being made for the resource.
 * @param midiInstrumentSamplePlayerHash
 */
async function loadInstrumentSamples(midiInstrumentSamplePlayerHash: IMidiInstrumentSamplePlayerHash){
  const promises = Object.entries(midiInstrumentSamplePlayerHash).map( ([_key, val]) => val.load());
  await Promise.all(promises);
}

/**
 * Midi velocities are between 0 - 127
 * GainNode volume is 0 - 1
 * @param velocity
 */
function convertMidiVelocityToVolume(velocity: number){
  const volume = velocity / 127;
  return volume;
}

function createConnectedGainNode({destination, context, volume =1, }: {context: AudioContext; destination: AudioNode; volume?: number}){
  const gainNode = context.createGain();
  gainNode.connect(destination);
  gainNode.gain.value = volume;
  return gainNode;
}


/**
 * Creates a hash object where key is the midi number and value is an instance of InstrumentSamplePlayer that has the url for the sound file for the midi number.
 * @param destination - node to connect the sample player to.  e.g. master gain node.
 */
function createMidiInstrumentSamplePlayerHashForOrangeTreeAcousticGuitar(destination: AudioNode): IMidiInstrumentSamplePlayerHash{
  const {baseUrl, guitar: {orangeTree: {guitarSoundsRelativePath, sampleFileNames: {loud: {fileType, loudRelativePath, midiHash}}}}} = soundAssets;
  const result = Object.entries(midiHash).reduce( (agg: IMidiInstrumentSamplePlayerHash, [midi, midiFileNamePart]) => {
    const url = `${baseUrl}${guitarSoundsRelativePath}${loudRelativePath}${midiFileNamePart}${fileType}`.replace(`#`, `%23`); //change the sharp '#' into '%23' so the file loads properly (# has a special function in urls)
    const midiNumber = parseInt(midi); //typescript incorrectly assumes that Object.entries uses string as property names.
    agg[midiNumber] = new InstrumentSamplePlayer({url, destination});
    return agg;
  }, {});
  return result;
}